<template>
  <div>
    <base-table
      ref="table"
      :pagination="pagination"
      @onPageChange="handlePageChange"
    >
      <template #thead>
        <thead>
          <tr>
            <th
              v-for="(col, colIndex) in headColumns"
              :key="colIndex"
              :style="col.style"
            >
              <span>
                <template v-if="colIndex === 0">
                  {{ col.text }}
                </template>
                <base-sort-label
                  v-else
                  v-model="sort[mapKey[colIndex]]"
                  @onChange="handleSortChange($event, mapKey[colIndex])"
                >
                  {{ col.text }}
                </base-sort-label>
              </span>
            </th>
          </tr>
        </thead>
      </template>
      <template #tbody>
        <tbody>
          <tr v-if="empty">
            <td
              colspan="6"
              class="text-center">
              {{ $t('message.notfounddata') }}
            </td>
          </tr>
          <tr
            v-for="(row, rowIndex) in rows"
            :key="rowIndex"
            @click="onClick($event, rowIndex)"
          >
            <td
              v-for="(col, colIndex) in 6"
              :key="colIndex"
            >
              <div
                :data-sku-id="row.sku_id"
                :style="[{ cursor: 'pointer' }]"
                class="sku"
                @mouseover="onMouseover($event, rowIndex, 0, '.sku', '#f9f9f9')"
                @mouseleave="onMouseleave($event, rowIndex, 0, '.sku')"
              >
                <span v-if="colIndex === 0">
                  <i :class="['fas fa-angle-right', { 'active': stateUIRows[rowIndex].showSkuId === row.sku_id }]"/>
                  {{ getProductFullName(row.product_name, row.sku.attributes) }}
                </span>
                <span v-else>
                  <u>{{ setToCustomDigitsNumberWithCheckEmpty(row[mapKey[colIndex]]) }}</u>
                </span>
                <div
                  v-for="(wh, whIndex) in row.warehouses"
                  v-if="stateUIRows[rowIndex].showSkuId === row.sku_id"
                  :key="whIndex"
                  :data-wh-id="wh.wh_id"
                  :style="[{ cursor: wh.bins.length ? 'pointer' : 'default' }]"
                  class="warehouse"
                  @mouseover="onMouseover($event, rowIndex, whIndex, '.warehouse', '#f2f2f2')"
                  @mouseleave="onMouseleave($event, rowIndex, whIndex, '.warehouse')"
                >
                  <span v-if="colIndex === 0">
                    <i
                      :class="['fas fa-angle-right', { 'active': stateUIRows[rowIndex].showWhId === wh.wh_id }]"
                      :style="[{ opacity: wh.bins.length ? 1 : 0 }]"/>
                    {{ wh.short_name }}
                  </span>
                  <span v-else>
                    {{ setToCustomDigitsNumberWithCheckEmpty(wh[mapKey[colIndex]]) }}
                  </span>
                  <div
                    v-for="(bin, binIndex) in wh.bins"
                    v-if="stateUIRows[rowIndex].showWhId === wh.wh_id"
                    :key="binIndex"
                    :data-bin-id="(bin.bin_location_id || 'null')"
                    :style="[{ cursor: bin.lots.length || bin.serials.length ? 'pointer' : 'default' }]"
                    class="bin"
                    @mouseover="onMouseover($event, rowIndex, binIndex, '.bin', '#e8e8e8')"
                    @mouseleave="onMouseleave($event, rowIndex, binIndex, '.bin')"
                  >
                    <span v-if="colIndex === 0">
                      <i
                        :class="['fas fa-angle-right', { 'active': stateUIRows[rowIndex].showBinId === (bin.bin_location_id || 'null') }]"
                        :style="[{ opacity: bin.lots.length || bin.serials.length ? 1 : 0 }]"/>
                      {{ bin.code || $t('apps.inventory.message.notspecified') }}
                    </span>
                    <span v-else>
                      {{ setToCustomDigitsNumberWithCheckEmpty(bin[mapKey[colIndex]]) }}
                    </span>
                    <div
                      v-for="(lot, lotIndex) in bin.lots"
                      v-if="stateUIRows[rowIndex].showBinId === (bin.bin_location_id || 'null')"
                      :key="lotIndex"
                      :style="[{ cursor: 'default' }]"
                      class="lot"
                      @mouseover="onMouseover($event, rowIndex, lotIndex, '.lot', '#dedede')"
                      @mouseleave="onMouseleave($event, rowIndex, lotIndex, '.lot')"
                    >
                      <span v-if="colIndex === 0">
                        {{ lot.lot_code }}
                      </span>
                      <span v-else>
                        <template v-if="colIndex === 3">
                          {{ setToCustomDigitsNumberWithCheckEmpty(lot.reserved_count) }}
                        </template>
                        <template v-else-if="colIndex === 5">
                          {{ setToCustomDigitsNumberWithCheckEmpty(lot.unit_balance) }}/{{ setToCustomDigitsNumberWithCheckEmpty(lot.unit_init) }}
                        </template>
                      </span>
                    </div>
                    <div
                      v-for="(ser, serIndex) in bin.serials"
                      v-if="stateUIRows[rowIndex].showBinId === (bin.bin_location_id || 'null')"
                      :key="serIndex"
                      :style="[{ cursor: 'default' }]"
                      class="serial"
                      @mouseover="onMouseover($event, rowIndex, serIndex, '.serial', '#dedede')"
                      @mouseleave="onMouseleave($event, rowIndex, serIndex, '.serial')"
                    >
                      <span v-if="colIndex === 0">
                        {{ ser.ser_code }}
                      </span>
                      <span v-else>
                        <template v-if="colIndex === 5">
                          {{ setToCustomDigitsNumberWithCheckEmpty(ser.unit_balance) }}
                        </template>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </base-table>
  </div>
</template>

<script>
export default {
  name: 'BaseTableCollapse',

  props: {
    headColumns: Array,
    rows: Array,
    pagination: Object,
    empty: Boolean,
    query: Object,
  },

  data () {
    return {
      mapKey: [null, 'lots_count', 'serials_count', 'reserved_count', 'out_hand_count', 'on_hand_count'],
      stateUIRows: [],
      sort: {
        lots_count: null,
        serials_count: null,
        reserved_count: null,
        out_hand_count: null,
        on_hand_count: 'desc',
      },
    }
  },

  created () {
    this.setState()
  },

  methods: {
    setState () {
      for (const item of this.rows) {
        this.stateUIRows.push({
          showSkuId: null,
          showWhId: null,
          showBinId: null,
        })
      }
      if (this.query.sort_key && this.query.sort_value) {
        this.sort[this.query.sort_key] = this.query.sort_value
      }
    },
    onClick (e, rowIndex) {
      for (const element of (e.path || e.composedPath())) {
        if (element.className === 'sku') {
          if (this.stateUIRows[rowIndex].showSkuId) {
            this.stateUIRows[rowIndex].showSkuId = null
          } else {
            this.stateUIRows[rowIndex].showSkuId = element.getAttribute('data-sku-id')
          }
          this.stateUIRows[rowIndex].showWhId = null
          this.stateUIRows[rowIndex].showBinId = null
          break
        } else if (element.className === 'warehouse') {
          if (this.stateUIRows[rowIndex].showWhId && this.stateUIRows[rowIndex].showWhId === element.getAttribute('data-wh-id')) {
            this.stateUIRows[rowIndex].showWhId = null
          } else {
            this.stateUIRows[rowIndex].showWhId = element.getAttribute('data-wh-id')
          }
          this.stateUIRows[rowIndex].showBinId = null
          break
        } else if (element.className === 'bin') {
          if (this.stateUIRows[rowIndex].showBinId && this.stateUIRows[rowIndex].showBinId === element.getAttribute('data-bin-id')) {
            this.stateUIRows[rowIndex].showBinId = null
          } else {
            this.stateUIRows[rowIndex].showBinId = element.getAttribute('data-bin-id')
          }
          break
        } else if (element.className === 'lot') {
          break
        } else if (element.className === 'serial') {
          break
        }
      }
    },
    onMouseover (e, rowIndex, noIndex, className, bgColor) {
      const table = this.$refs.table.$el
      const tr = table.querySelectorAll('tbody tr')[rowIndex]
      const td = tr.querySelectorAll('td')
      for (const ele of td) {
        ele.querySelectorAll(className)[noIndex].style.backgroundColor = bgColor
      }
    },
    onMouseleave (e, rowIndex, noIndex, className) {
      const table = this.$refs.table.$el
      const tr = table.querySelectorAll('tbody tr')[rowIndex]
      const td = tr.querySelectorAll('td')
      for (const ele of td) {
        ele.querySelectorAll(className)[noIndex].style.backgroundColor = ''
      }
    },
    handleSortChange (sortValue, mapKey) {
      for (const key of Object.keys(this.sort)) {
        if (key !== mapKey) {
          this.sort[key] = null
        }
      }
      this.$emit('onSortChange', { key: mapKey, value: sortValue })
    },
    handlePageChange (pageNum) {
      this.$emit('onPageChange', pageNum)
    },
  },
}
</script>

<style scoped>
tr:hover {
  background: none !important;
}

tr th, tr td {
  padding: 6px 0 6px 0;
}

tr div {
  padding-top: 3px;
  line-height: 24px;
}

tr span {
  padding-left: 10px;
  padding-right: 10px;
}

tr td:first-of-type .warehouse span:first-of-type {
  margin-left: 12px;
}

tr td:first-of-type .bin span:first-of-type {
  margin-left: 24px;
}

tr td:first-of-type .lot span:first-of-type,
tr td:first-of-type .serial span:first-of-type {
  margin-left: 36px;
}

i.active {
  transform: rotate(90deg);
}
</style>



// WEBPACK FOOTER //
// src/components/base/BaseTableSkuLevel.vue